import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ensureAcceptJs } from '../services/acceptJs';
import {
  CreditCard,
  FileText,
  Plus,
  ChevronRight,
  Loader2,
  RefreshCw,
  Info,
} from 'lucide-react';
import apiService from '../services/api';
import billingApi, { BillingHistoryParams } from '../services/billingApi';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useSoftLaunch } from '../hooks';

import BillingAccountCard from '../components/billing/BillingAccountCard';
import BillingAccountForm from '../components/billing/BillingAccountForm';
import PaymentMethodForm, { PaymentFormSubmitData } from '../components/billing/PaymentMethodForm';
import BillingSummaryCards from '../components/billing/BillingSummaryCards';
import BillingEventsTable from '../components/billing/BillingEventsTable';
import {
  BillingAccount,
  BillingAccountFormData,
  BillingEvent,
  BillingSummary,
  PaymentIntentResponse,
  CardBillingInfo,
} from '../components/billing/billingTypes';

declare global {
  interface Window {
    Accept: any;
  }
}

type TabType = 'accounts' | 'history';

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { serverRegion, agencyRole } = useAuth();
  const { showSuccess, showError } = useToast();
const { isEnabled: isSoftLaunch } = useSoftLaunch();
  // Get initial tab from URL or default to 'accounts'
  const initialTab = searchParams.get('workspace_id') ? 'history' : ((searchParams.get('tab') as TabType) || 'accounts');  
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [loading, setLoading] = useState(false);

  // Billing Accounts
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BillingAccount | null>(null);

  // Payment Method
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntentResponse | null>(null);
  const [initialBillingInfo, setInitialBillingInfo] = useState<CardBillingInfo | undefined>(undefined);

  // Billing History
  const [events, setEvents] = useState<BillingEvent[]>([]);
const [summary, setSummary] = useState<BillingSummary>({
    total_paid: 0,
    total_credits: 0,
    credits_available: 0,
    total_refunds: 0,
    outstanding: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
const [historyFilters, setHistoryFilters] = useState({
    billingProfileId: null as number | null,
    workspaceId: searchParams.get('workspace_id') ? parseInt(searchParams.get('workspace_id')!) : null,
    type: null as string | null,
    status: null as string | null,
    from: null as string | null,
    to: null as string | null,
  });

  // Workspaces for filter dropdown
  const [workspaces, setWorkspaces] = useState<{ id: number; name: string }[]>([]);

  // Hover states
  const [addHovered, setAddHovered] = useState(false);
  const [hoveredTab, setHoveredTab] = useState<TabType | null>(null);
  const [syncHovered, setSyncHovered] = useState(false);

  // Access control
  const canAccessBilling = agencyRole === 'owner' || agencyRole === 'billing_admin';

  useEffect(() => {
    if (!canAccessBilling) {
      navigate('/dashboard');
      return;
    }
    loadAccounts();
    loadWorkspaces();
  }, [canAccessBilling]);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (activeTab === 'accounts') {
      params.delete('tab');
    } else {
      params.set('tab', activeTab);
    }
    setSearchParams(params, { replace: true });
  }, [activeTab]);

  // Load history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, historyFilters, pagination.page]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBillingProfiles();
      setAccounts(data.profiles || []);
    } catch (err: any) {
      showError(err.message || 'Failed to load billing accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspaces = async () => {
    try {
      const data = await billingApi.getWorkspaces();
      setWorkspaces(data.map((w: any) => ({ id: w.id, name: w.name })));
    } catch (err: any) {
      console.error('Failed to load workspaces:', err);
    }
  };

const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      await billingApi.syncHistory();
      const params: BillingHistoryParams = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (historyFilters.billingProfileId) params.billing_profile_id = historyFilters.billingProfileId;
      if (historyFilters.workspaceId) params.workspace_id = historyFilters.workspaceId;
if (historyFilters.type) params.type = historyFilters.type;
      if (historyFilters.status) params.status = historyFilters.status;
      if (historyFilters.from) params.from = historyFilters.from;
      if (historyFilters.to) params.to = historyFilters.to;

      const response = await billingApi.getHistory(params);
      setEvents(response.events);
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (err: any) {
      showError(err.message || 'Failed to load billing history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilters, pagination.page, pagination.limit]);

  // Account handlers
  const handleCreateAccount = async (data: BillingAccountFormData) => {
    try {
      setLoading(true);
      await apiService.createBillingProfile(data);
      showSuccess('Billing account created');
      setShowAccountForm(false);
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to create billing account');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async (data: BillingAccountFormData) => {
    if (!editingAccount) return;
    try {
      setLoading(true);
await apiService.updateBillingProfile(editingAccount.id, data);
      showSuccess('Billing account updated');
      setShowAccountForm(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to update billing account');
    } finally {
      setLoading(false);
    }
  };

 const handleDeleteAccount = async (id: number) => {
    try {
      setLoading(true);
      await apiService.deleteBillingProfile(id);
      showSuccess('Billing account deleted');
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to delete billing account');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAccount = async (id: number) => {
    try {
      setLoading(true);
      await apiService.setDefaultBillingProfile(id);
      showSuccess('Default billing account updated');
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to set default account');
    } finally {
      setLoading(false);
    }
  };

  // Payment method handlers
const handleAddPaymentMethod = async (accountId: number) => {
  const account = accounts.find((a) => a.id === accountId);
  try {
    setLoading(true);
    const response = await apiService.createPaymentIntent(accountId);
	setPaymentIntent(response.paymentIntent);
      if (response.paymentIntent?.gateway === 'authnet') ensureAcceptJs(response.paymentIntent.environment).catch(() => {});
      setPaymentAccountId(accountId);

      if (account) {
        setInitialBillingInfo({
          firstName: account.first_name || '',
          lastName: account.last_name || '',
          addressLine1: account.address_line1 || '',
          addressLine2: account.address_line2 || '',
          city: account.city || '',
          state: account.state || '',
          postalCode: account.postal_code || '',
country: account.country || '',
        });
      }

      setShowPaymentForm(true);
    } catch (err: any) {
      showError(err.message || 'Failed to initialize payment form');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePaymentMethod = async (data: PaymentFormSubmitData) => {
    if (!paymentIntent || !paymentAccountId) return;

    try {
      setLoading(true);

if (!data.billingInfo.addressLine1 || !data.billingInfo.city || !data.billingInfo.country) {
        showError('Please fill in country, address, and city');
        setLoading(false);
        return;
      }

      if (data.gateway === 'stripe') {
        if (!data.stripeElements || !data.stripeInstance || !data.clientSecret) {
          showError('Stripe not properly initialized');
          setLoading(false);
          return;
        }

        if (!data.billingInfo.postalCode || !data.billingInfo.postalCode.trim()) {
          showError("Postal code is required");
          setLoading(false);
          return;
        }

        const billingDetails = {
          name: `${data.billingInfo.firstName} ${data.billingInfo.lastName}`.trim(),
          address: {
            line1: data.billingInfo.addressLine1,
            line2: data.billingInfo.addressLine2 || '',
            city: data.billingInfo.city,
            state: data.billingInfo.state || '',
            postal_code: data.billingInfo.postalCode,
            country: data.billingInfo.country,
          },
        };

        const { error: stripeError, setupIntent } = await data.stripeInstance.confirmCardSetup(data.clientSecret, {
          payment_method: { card: data.stripeElements, billing_details: billingDetails },
        });

        if (stripeError) {
          showError(stripeError.message || 'Card verification failed');
          setLoading(false);
          return;
        }

        await apiService.addStripePaymentMethod(paymentAccountId, setupIntent.id, paymentIntent.gatewayId, {
          firstName: data.billingInfo.firstName,
          lastName: data.billingInfo.lastName,
          line1: data.billingInfo.addressLine1,
          line2: data.billingInfo.addressLine2,
          city: data.billingInfo.city,
          state: data.billingInfo.state,
          zip: data.billingInfo.postalCode,
          country: data.billingInfo.country,
        });
      } else {
        if (
          !data.authnetCard?.cardNumber ||
          !data.authnetCard?.expMonth ||
          !data.authnetCard?.expYear ||
          !data.authnetCard?.cvv
        ) {
          showError('Please fill in all card details');
          setLoading(false);
          return;
        }

await ensureAcceptJs(paymentIntent.environment);
        const secureData = {
          authData: {
            apiLoginID: paymentIntent.apiLoginId,
            clientKey: paymentIntent.clientKey,
          },
          cardData: {
            cardNumber: data.authnetCard.cardNumber.replace(/\s/g, ''),
            month: data.authnetCard.expMonth,
            year: data.authnetCard.expYear,
            cardCode: data.authnetCard.cvv,
          },
        };

        const opaqueData = await new Promise<any>((resolve, reject) => {
          window.Accept.dispatchData(secureData, (response: any) => {
            if (response.messages.resultCode === 'Error') {
              reject(new Error(response.messages.message.map((m: any) => m.text).join(', ')));
            } else {
              resolve({
                dataDescriptor: response.opaqueData.dataDescriptor,
                dataValue: response.opaqueData.dataValue,
                last4: data.authnetCard!.cardNumber.slice(-4),
                expMonth: parseInt(data.authnetCard!.expMonth),
                expYear: parseInt(data.authnetCard!.expYear),
              });
            }
          });
        });

        await apiService.addAuthnetPaymentMethod(
          paymentAccountId,
          {
            ...opaqueData,
            billingAddress: {
              firstName: data.billingInfo.firstName,
              lastName: data.billingInfo.lastName,
              line1: data.billingInfo.addressLine1,
              line2: data.billingInfo.addressLine2,
              city: data.billingInfo.city,
              state: data.billingInfo.state,
              zip: data.billingInfo.postalCode,
              country: data.billingInfo.country,
            },
          },
          paymentIntent.gatewayId
        );
      }

      showSuccess('Payment method added successfully');
      handleClosePaymentForm();
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  const handleClosePaymentForm = () => {
    setShowPaymentForm(false);
    setPaymentAccountId(null);
    setPaymentIntent(null);
    setInitialBillingInfo(undefined);
  };

  const handleSetDefaultPaymentMethod = async (id: number) => {
    try {
      setLoading(true);
      await apiService.setDefaultPaymentMethod(id);
      showSuccess('Default payment method updated');
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to set default payment method');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;
    try {
      setLoading(true);
      await apiService.deletePaymentMethod(id);
      showSuccess('Payment method deleted');
      loadAccounts();
    } catch (err: any) {
      showError(err.message || 'Failed to delete payment method');
    } finally {
      setLoading(false);
    }
  };

  // History handlers
  const handleSync = async () => {
    setSyncing(true);
    try {
      await billingApi.syncHistory();
      await loadHistory();
      showSuccess('Billing history synced');
    } catch (err: any) {
      showError(err.message || 'Failed to sync billing history');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownload = async (eventId: number) => {
    setDownloadingId(eventId);
    try {
      const url = await billingApi.getDownloadUrl(eventId);
      window.open(url, '_blank');
    } catch (err: any) {
      showError(err.message || 'Failed to get download URL');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFilterChange = (newFilters: typeof historyFilters) => {
    setHistoryFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const tabs = [
    { id: 'accounts' as TabType, label: 'Billing Accounts', icon: CreditCard },
    { id: 'history' as TabType, label: 'Billing History', icon: FileText },
  ];

  if (!canAccessBilling) return null;

  return (
    <div
      style={{
        padding: '32px',
        maxWidth: '1400px',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
          color: 'var(--v2-text-tertiary)',
          marginBottom: '24px',
        }}
      >
        <span
          onClick={() => navigate('/dashboard')}
          style={{
            color: 'var(--v2-text-tertiary)',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseOut={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text)' }}>Billing</span>
      </div>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--v2-text)',
              margin: 0,
            }}
          >
            Billing
          </h1>
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--v2-text-tertiary)',
              marginTop: '4px',
            }}
          >
            Manage your billing accounts, payment methods, and view billing history
          </p>
        </div>
        {activeTab === 'accounts' && (
          <button
            onClick={() => {
              setEditingAccount(null);
              setShowAccountForm(true);
            }}
            onMouseOver={() => setAddHovered(true)}
            onMouseOut={() => setAddHovered(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 18px',
              backgroundColor: addHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            <Plus size={16} strokeWidth={2} />
            Add Billing Account
          </button>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--v2-border)',
          marginBottom: '24px',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isHovered = hoveredTab === tab.id;
          const TabIcon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: isHovered && !isActive ? 'var(--v2-surface-alt)' : 'transparent',
                color: isActive ? 'var(--v2-primary)' : isHovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'all 0.15s',
                borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
              }}
            >
              <TabIcon size={16} strokeWidth={1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Billing Accounts Tab */}
{activeTab === 'accounts' && (
        <div>
          {/* Early Access Billing Notice */}
{isSoftLaunch && !workspaces.some((w: any) => w.is_lifetime) && (
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #fcd34d',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}>
              <CreditCard size={20} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px 0' }}>
                  Payment methods not required during Early Access
                </p>
                <p style={{ fontSize: '0.8125rem', color: '#a16207', margin: 0, lineHeight: 1.5 }}>
                  Enjoy full access without entering payment details. We'll notify you before your Early Access period ends with options to continue.
                </p>
              </div>
</div>
          )}
          {accounts.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                backgroundColor: 'var(--v2-primary-light)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 16px',
                marginBottom: '24px',
                fontSize: '0.8125rem',
                color: 'var(--v2-text)',
              }}
            >
              <Info size={18} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                Your billing <strong>country</strong> and <strong>VAT/Tax ID</strong> on each account determine the
                tax on your invoices. Open an account below to confirm them — businesses can add a VAT/Tax ID to apply
                reverse charge (no tax added).
              </span>
            </div>
          )}
          {loading && accounts.length === 0 ? (
            <div
              style={{
                backgroundColor: 'var(--v2-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--v2-border)',
                padding: '60px 20px',
                textAlign: 'center',
              }}
            >
              <Loader2
                size={32}
                strokeWidth={2}
                color="var(--v2-primary)"
                style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}
              />
              <p style={{ color: 'var(--v2-text-tertiary)', margin: 0 }}>Loading billing accounts...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : accounts.length === 0 ? (
            <div
              style={{
                backgroundColor: 'var(--v2-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--v2-border)',
                padding: '60px 20px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--v2-surface-alt)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <CreditCard size={28} strokeWidth={1.5} color="var(--v2-text-muted)" />
              </div>
              <p
                style={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--v2-text)',
                  marginBottom: '8px',
                }}
              >
                No billing accounts yet
              </p>
              <p style={{ color: 'var(--v2-text-tertiary)', margin: '0 0 20px 0' }}>
                Create a billing account to manage payments for your workspaces.
              </p>
              <button
                onClick={() => {
                  setEditingAccount(null);
                  setShowAccountForm(true);
                }}
                onMouseOver={() => setAddHovered(true)}
                onMouseOut={() => setAddHovered(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 18px',
                  backgroundColor: addHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                }}
              >
                <Plus size={16} strokeWidth={2} />
                Add Billing Account
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
{accounts.map((account) => (
                <BillingAccountCard
                  key={account.id}
                  account={account}
                  onEdit={(acc) => {
                    setEditingAccount(acc);
                    setShowAccountForm(true);
                  }}
                  onDelete={handleDeleteAccount}
                  onSetDefault={handleSetDefaultAccount}
                  onAddPaymentMethod={handleAddPaymentMethod}
                  onSetDefaultPaymentMethod={handleSetDefaultPaymentMethod}
onDeletePaymentMethod={handleDeletePaymentMethod}
                  onGetCredits={billingApi.getProfileCredits}
                  loading={loading}
                  isOnlyAccount={accounts.length === 1}
softLaunchMode={isSoftLaunch && !workspaces.some((w: any) => w.is_lifetime)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Billing History Tab */}
      {activeTab === 'history' && (
        <div>
          <BillingSummaryCards summary={summary} loading={historyLoading} />
          <BillingEventsTable
            events={events}
            billingProfiles={accounts.map((a) => ({ id: a.id, name: a.name }))}
            workspaces={workspaces}
            filters={historyFilters}
            onFilterChange={handleFilterChange}
            onDownload={handleDownload}
            loading={historyLoading}
            downloadingId={downloadingId}
          />
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
              }}
            >
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--v2-surface)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  color: pagination.page <= 1 ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)', padding: '0 12px' }}>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--v2-surface)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  color: pagination.page >= pagination.pages ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Billing Account Form Modal */}
      <BillingAccountForm
        isOpen={showAccountForm}
        onClose={() => {
          setShowAccountForm(false);
          setEditingAccount(null);
        }}
        onSave={editingAccount ? handleUpdateAccount : handleCreateAccount}
        editingAccount={editingAccount}
        serverRegion={serverRegion || 'US'}
        loading={loading}
      />

      {/* Payment Method Form Modal */}
      <PaymentMethodForm
        isOpen={showPaymentForm}
        onClose={handleClosePaymentForm}
        onSave={handleSavePaymentMethod}
        paymentIntent={paymentIntent}
        serverRegion={serverRegion || 'US'}
        initialBillingInfo={initialBillingInfo}
        loading={loading}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BillingPage;
